import { Mark, mergeAttributes } from '@tiptap/core';

export interface CitationOptions {
  HTMLAttributes: Record<string, any>;
  onCitationClick?: (attrs: CitationAttributes) => void;
}

export interface CitationAttributes {
  chronologyEventId: string;
  documentId: string;
  documentName: string;
  pageNumber: number;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    citation: {
      /**
       * Set a citation mark
       */
      setCitation: (attributes: CitationAttributes) => ReturnType;
      /**
       * Toggle a citation mark
       */
      toggleCitation: (attributes: CitationAttributes) => ReturnType;
      /**
       * Unset a citation mark
       */
      unsetCitation: () => ReturnType;
    };
  }
}

export const Citation = Mark.create<CitationOptions>({
  name: 'citation',

  addOptions() {
    return {
      HTMLAttributes: {},
      onCitationClick: undefined,
    };
  },

  addAttributes() {
    return {
      chronologyEventId: {
        default: null,
        parseHTML: element => element.getAttribute('data-chronology-event-id'),
        renderHTML: attributes => {
          if (!attributes.chronologyEventId) {
            return {};
          }
          return {
            'data-chronology-event-id': attributes.chronologyEventId,
          };
        },
      },
      documentId: {
        default: null,
        parseHTML: element => element.getAttribute('data-document-id'),
        renderHTML: attributes => {
          if (!attributes.documentId) {
            return {};
          }
          return {
            'data-document-id': attributes.documentId,
          };
        },
      },
      documentName: {
        default: null,
        parseHTML: element => element.getAttribute('data-document-name'),
        renderHTML: attributes => {
          if (!attributes.documentName) {
            return {};
          }
          return {
            'data-document-name': attributes.documentName,
          };
        },
      },
      pageNumber: {
        default: null,
        parseHTML: element => {
          const page = element.getAttribute('data-page-number');
          return page ? parseInt(page, 10) : null;
        },
        renderHTML: attributes => {
          if (!attributes.pageNumber) {
            return {};
          }
          return {
            'data-page-number': attributes.pageNumber,
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-citation]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        'data-citation': '',
        class: 'citation-mark',
      }),
      0,
    ];
  },

  addCommands() {
    return {
      setCitation:
        attributes =>
        ({ commands }) => {
          return commands.setMark(this.name, attributes);
        },
      toggleCitation:
        attributes =>
        ({ commands }) => {
          return commands.toggleMark(this.name, attributes);
        },
      unsetCitation:
        () =>
        ({ commands }) => {
          return commands.unsetMark(this.name);
        },
    };
  },
});
